<template>
  <b-modal
    id="chat-agent-step-modal"
    ref="chatAgentStepModal"
    :title="
      isEdit ? $t('title.editWorkflowStep') : $t('button.addWorkflowStep')
    "
    size="lg"
    hide-footer
    content-class="shadow"
    no-close-on-backdrop
    @hide="onModalHide"
  >
    <validation-observer
      ref="stepForm"
      v-slot="{ invalid }"
    >
      <b-form @submit.prevent="validateAndSubmit">
        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.name')"
          rules="required|min:3|max:47"
        >
          <b-form-group
            :label="$t('field.name')"
            class="mt-1"
          >
            <b-form-input
              v-model="step.name"
              :placeholder="$t('placeholder.stepName')"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.ability')"
          rules="required|min:10|max:300"
        >
          <b-form-group
            :label="$t('field.ability')"
            class="mt-2"
          >
            <b-form-textarea
              v-model="step.description"
              :placeholder="$t('placeholder.abilityDescription')"
              :state="errors.length > 0 ? false : null"
              rows="3"
              max-rows="6"
              trim
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <!-- Question Type Selection -->
        <div class="question-section border p-2 mb-3">
          <b-form-group
            :label="$t('field.questionType')"
            class="mt-2"
          >
            <b-form-radio-group
              v-model="step.questionType"
              class="mt-1"
              :options="questionTypeOptions"
              name="question-type"
              @change="handleQuestionTypeChange"
            />
          </b-form-group>

          <!-- Multiple Choice Answers (shown only for multiple choice types) -->
          <template v-if="step.questionType !== QUESTION_TYPE.OPEN && step.questionType !== QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT && step.questionType !== QUESTION_TYPE.ATTACHMENTS">
            <b-form-group
              :label="$t('field.answers')"
              class="mt-2"
            >
              <b-row
                v-for="(answer, answerIndex) in step.answers"
                :key="answerIndex"
                class="mb-1"
              >
                <b-col>
                  <validation-provider
                    v-slot="{ errors }"
                    :name="$t('field.answer') + ' ' + (answerIndex + 1)"
                    :rules="locale === $i18n.locale.split('-')[0] ? 'required|min:3|max:47' : 'min:3|max:47'"
                  >
                    <div class="d-flex">
                      <div class="flex-grow-1 mr-1">
                        <b-form-input
                          v-model="answer.answer[locale]"
                          :placeholder="$t('placeholder.answer')"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </div>
                      <div style="min-width: 65px">
                        <b-form-select
                          v-model="locale"
                          :options="locales"
                          :style="{
                            width: '65px'
                          }"
                        />
                      </div>
                    </div>
                  </validation-provider>
                </b-col>
                <b-col
                  cols="auto"
                  class="pl-0 d-flex align-items-start"
                  style="padding-top: 2px;"
                >
                  <b-button
                    v-if="step.answers.length > 1"
                    variant="danger"
                    pill
                    size="sm"
                    :style="{
                      padding: '5px',
                      height: '28px',
                      width: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }"
                    @click="removeAnswer(answerIndex)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      :style="{
                        width: '16px',
                        height: '16px'
                      }"
                    />
                  </b-button>
                </b-col>
              </b-row>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="addAnswer"
              >
                <feather-icon icon="PlusIcon" />
                {{ $t('button.addAnswer') }}
              </b-button>
            </b-form-group>
          </template>
          <!-- Parameters -->
          <template v-if="step.questionType === QUESTION_TYPE.OPEN || step.questionType === QUESTION_TYPE.ATTACHMENTS">
            <b-form-group
              :label="$t('field.parameters')"
              class="mt-2"
            >
              <b-row
                v-for="(param, paramIndex) in step.parameters"
                :key="paramIndex"
                class="mb-2"
              >
                <b-col cols="5">
                  <validation-provider
                    v-slot="{ errors }"
                    :name="`param${paramIndex}Name`"
                    :rules="getParamLocale(paramIndex) === $i18n.locale.split('-')[0] ? 'required|min:3|max:47' : 'min:3|max:47'"
                  >
                    <div class="d-flex">
                      <div class="flex-grow-1 mr-1">
                        <b-form-input
                          :value="param.nameTranslate[getParamLocale(paramIndex)]"
                          @input="updateParamName(paramIndex, getParamLocale(paramIndex), $event)"
                          :placeholder="$t('placeholder.paramName')"
                          :state="errors.length > 0 ? false : null"
                          :readonly="step.questionType === QUESTION_TYPE.ATTACHMENTS"
                        />
                        <small class="text-danger">{{
                          getPramNameError(errors[0], `param${paramIndex}Name`)
                        }}</small>
                      </div>
                      <div style="min-width: 65px">
                        <b-form-select
                          :value="getParamLocale(paramIndex)"
                          @input="updateParamLocale(paramIndex, $event)"
                          :options="locales"
                          :style="{
                            width: '65px'
                          }"
                        />
                      </div>
                    </div>
                  </validation-provider>
                </b-col>
                <b-col cols="5">
                  <validation-provider
                    v-slot="{ errors }"
                    :name="`param${paramIndex}Key`"
                    rules="required|param_key|min:3|max:47"
                  >
                    <b-form-input
                      v-model="param.key"
                      :placeholder="$t('placeholder.paramKey')"
                      :state="errors.length > 0 ? false : null"
                      :readonly="step.questionType === QUESTION_TYPE.ATTACHMENTS"
                    />
                    <small class="text-danger">{{
                      getPramKeyError(errors[0], `param${paramIndex}Key`)
                    }}</small>
                  </validation-provider>
                </b-col>
                <b-col class="d-flex align-items-center">
                  <b-button
                    v-if="step.parameters.length > 1"
                    variant="danger"
                    pill
                    size="sm"
                    :style="{
                      padding: '5px'
                    }"
                    @click="removeParameter(paramIndex)"
                  >
                    <feather-icon icon="TrashIcon" />
                  </b-button>
                </b-col>
              </b-row>
              <b-button
                v-if="step.questionType !== QUESTION_TYPE.ATTACHMENTS"
                variant="outline-secondary"
                size="sm"
                @click="addParameter(step)"
              >
                <feather-icon icon="PlusIcon" />
                {{ $t('button.addParameter') }}
              </b-button>
            </b-form-group>
          </template>
          <template v-if="step.questionType === QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT">
            <div class="d-flex align-items-center mb-2">
              <feather-icon
                icon="InfoIcon"
                class="text-primary mr-1"
                :style="{
                  width: '16px',
                  height: '16px'
                }"
              />
              <span class="text-muted">{{ $t('field.product_fetched_from_remote') }}</span>
            </div>
          </template>
        </div>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.callbackUrl')"
          rules="required|url"
        >
          <b-form-group
            :label="$t('field.callbackUrl')"
            class="mt-2"
          >
            <b-form-input
              v-model="step.callbackUrl"
              :placeholder="$t('placeholder.callbackUrl')"
              type="url"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="closeModal"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="invalid || loading || !isAnswersValid"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ isEdit ? $t('button.update') : $t('button.save') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BRow,
  BCol,
  BModal,
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BFormTextarea,
  BFormRadioGroup,
  BFormRadio,
  BFormSelect,
} from 'bootstrap-vue'
import './validateRules'
// import NSingleSelect from '@/components/NSingleSelect'

export default {
  name: 'ChatAgentStepModal',

  components: {
    BRow,
    BCol,
    BModal,
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BFormRadioGroup,
    BFormRadio,
    BFormSelect,
    // NSingleSelect,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    selectedStep: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      required: false,
    },
  },

  data() {
    return {
      step: {
        name: '',
        description: '',
        parameters: [
          {
            key: '',
            nameTranslate: {
              en: '',
              km: '',
              zh: '',
              vi: '',
            },
          },
        ],
        callbackUrl: '',
        parameterOption: 'extract_parameter',
        questionType: 1, // Default to OPEN
        answers: [], // Initialize as empty array
      },
      loading: false,
      options: [
        { label: 'extract_parameter', value: 'extract_parameter' },
        { label: 'single_choice', value: 'single_choice' },
        { label: 'multiple_choice', value: 'status' },
      ],
      QUESTION_TYPE: {
        OPEN: 1,
        MULTI_CHOICE_SINGLE_ANSWER: 2,
        MULTI_CHOICE_MULTI_ANSWER: 3,
        MULTI_CHOICE_FOR_PRODUCT: 6,
        ATTACHMENTS: 7,
      },
      questionTypeOptions: [
        { text: this.$t('field.openQuestion'), value: 1 },
        { text: this.$t('field.singleChoice'), value: 2 },
        { text: this.$t('field.multipleChoice'), value: 3 },
        { text: this.$t('field.multipleChoiceForProduct'), value: 6 },
        { text: this.$t('field.attachments'), value: 7 },
      ],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
      locale: this.$i18n.locale.split('-')[0],
      paramLocales: {}, // Store locale for each parameter
      prevParameters: [],
      prevQuestionType: 1,
    }
  },

  computed: {
    isAnswersValid() {
      // Return true for open questions
      if (this.step.questionType === this.QUESTION_TYPE.OPEN || this.step.questionType === this.QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT || this.step.questionType === this.QUESTION_TYPE.ATTACHMENTS) {
        return true
      }

      // For multiple choice questions, check if current locale answers exist
      const currentLocale = this.$i18n.locale.split('-')[0]
      return this.step.answers
             && this.step.answers.length > 0
             && this.step.answers.every(answer => answer.answer
               && answer.answer[currentLocale]
               && answer.answer[currentLocale].trim() !== '')
    },
  },

  watch: {
    show(val) {
      if (val) {
        this.step = {
          name: '',
          description: '',
          parameters: [
            {
              key: '',
              nameTranslate: {
                en: '',
                km: '',
                zh: '',
                vi: '',
              },
            },
          ],
          callbackUrl: '',
          questionType: 1, // Default to OPEN
          answers: [], // Initialize as empty array
        }
        // Reset param locales
        this.paramLocales = {}
        this.$set(this.paramLocales, 0, this.$i18n.locale.split('-')[0])
        this.showModal()
        if (this.isEdit) {
          this.initializeStepData()
        }
      }
    },
  },

  methods: {
    initializeStepData() {
      this.prevQuestionType = this.selectedStep.questionType
      
      // Convert old parameter format to new nameTranslate format
      const convertedParameters = []
      if (this.selectedStep.parameters && Array.isArray(this.selectedStep.parameters)) {
        this.selectedStep.parameters.forEach(param => {
          const nameTranslate = param.nameTranslate || {}
          const convertedParam = {
            key: param.key || '',
            nameTranslate: {
              en: nameTranslate.en || param.name || '',
              km: nameTranslate.km || param.name || '',
              zh: nameTranslate.zh || param.name || '',
              vi: nameTranslate.vi || param.name || '',
            },
          }
          convertedParameters.push(convertedParam)
        })
      }
      
      // Store converted parameters for restoration
      this.prevParameters = JSON.parse(JSON.stringify(convertedParameters))
      
      this.step = {
        ...this.selectedStep,
        parameters: convertedParameters,
        questionType: this.selectedStep.questionType || this.QUESTION_TYPE.OPEN,
        answers: Array.isArray(this.selectedStep.answers)
          ? this.selectedStep.answers.map(answer => {
            // Ensure all locales have default values to prevent undefined errors
            const answerObj = answer.answer || answer.text || answer || {}
            return {
              answer: {
                en: answerObj.en || '',
                zh: answerObj.zh || '',
                km: answerObj.km || '',
                vi: answerObj.vi || '',
              },
            }
          }) : [],
      }
      if (this.step.questionType !== this.QUESTION_TYPE.OPEN && this.step.questionType !== this.QUESTION_TYPE.ATTACHMENTS) {
        this.step.parameters = [
          {
            key: '',
            nameTranslate: {
              en: '',
              km: '',
              zh: '',
              vi: '',
            },
          },
        ]
      }
      // Set initial locale to match user's current locale
      this.locale = this.$i18n.locale.split('-')[0] || 'en'
      
      // Initialize param locales
      this.paramLocales = {}
      this.step.parameters.forEach((_, index) => {
        this.$set(this.paramLocales, index, this.$i18n.locale.split('-')[0])
      })
    },

    handleQuestionTypeChange(value) {
      if (value === this.QUESTION_TYPE.ATTACHMENTS) {
        this.step.parameters = [
          {
            key: 'files',
            nameTranslate: {
              en: 'Files',
              km: 'ឯកសារ',
              zh: '文件',
              vi: 'Tập tin',
            },
          },
        ]
        // Initialize locale for files parameter
        this.$set(this.paramLocales, 0, this.$i18n.locale.split('-')[0])
        return
      }
      if (value !== this.QUESTION_TYPE.OPEN && value !== this.QUESTION_TYPE.ATTACHMENTS && this.step.answers.length === 0) {
        // Initialize with one empty answer for multiple choice
        this.step.answers = [
          {
            answer: {
              en: '',
              zh: '',
              km: '',
              vi: '',
            },
          },
        ]
      } else if (value === this.QUESTION_TYPE.OPEN || value === this.QUESTION_TYPE.ATTACHMENTS) {
        this.step.answers = []
      }
    },

    createDefaultParameter() {
      return {
        key: '',
        nameTranslate: {
          en: '',
          km: '',
          zh: '',
          vi: '',
        },
      }
    },

    addParameter() {
      const newIndex = this.step.parameters.length
      this.step.parameters.push(this.createDefaultParameter())
      // Initialize locale for new parameter
      this.$set(this.paramLocales, newIndex, this.$i18n.locale.split('-')[0])
    },

    removeParameter(paramIndex) {
      if (this.step.parameters.length > 1) {
        this.step.parameters.splice(paramIndex, 1)
        // Remove corresponding locale
        this.$delete(this.paramLocales, paramIndex)
        // Reindex remaining locales
        const newLocales = {}
        Object.keys(this.paramLocales).forEach(key => {
          const index = parseInt(key)
          if (index > paramIndex) {
            newLocales[index - 1] = this.paramLocales[key]
          } else if (index < paramIndex) {
            newLocales[index] = this.paramLocales[key]
          }
        })
        this.paramLocales = newLocales
      } else {
        this.$bvToast.toast(this.$t('message.atLeastOneParameter'), {
          variant: 'warning',
        })
      }
    },

    addAnswer() {
      this.step.answers.push({
        answer: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
      })
    },

    removeAnswer(index) {
      if (this.step.answers.length > 1) {
        this.step.answers.splice(index, 1)
      } else {
        this.$bvToast.toast(this.$t('message.atLeastOneAnswer'), {
          variant: 'warning',
        })
      }
    },

    validateAndSubmit() {
      this.$refs.stepForm.validate().then(success => {
        if (this.step.questionType !== this.QUESTION_TYPE.OPEN && this.step.questionType !== this.QUESTION_TYPE.ATTACHMENTS) {
          this.step.parameters = [
            {
              key: 'answer',
              nameTranslate: {
                en: 'Answer',
                km: 'ចម្លើយ',
                zh: '答案',
                vi: 'Câu trả lời',
              },
            },
          ]
        } else if (this.prevQuestionType === this.step.questionType && this.prevQuestionType !== this.QUESTION_TYPE.OPEN && this.prevQuestionType !== this.QUESTION_TYPE.ATTACHMENTS && this.prevParameters.length > 0) {
          // Only restore prevParameters if switching FROM a choice type TO the same choice type
          // This prevents overwriting edited parameters when saving
          this.step.parameters = this.prevParameters
        }

        if (success) {
          if (!this.isEdit) {
            this.$emit('save-step', this.step)
          } else {
            this.$emit('update-step', this.step)
          }
          console.log('submit success: step', this.step)
          this.closeModal()
        }
      })
    },

    async updateStep() {},

    closeModal() {
      this.$refs.chatAgentStepModal.hide()
      this.$emit('hide-modal')
    },

    showModal() {
      this.$refs.chatAgentStepModal.show()
    },

    onModalHide() {
      this.$emit('hide-modal')
    },
    getPramNameError(error, name) {
      if (error) {
        console.log(error.includes(name))
        return error?.includes(name)
          ? error.replace(name, this.$t('field.name'))
          : error
      }
      return ''
    },
    getPramKeyError(error, name) {
      if (error) {
        return error?.includes(name)
          ? error.replace(name, this.$t('field.key'))
          : error
      }
      return ''
    },
    
    getParamLocale(paramIndex) {
      return this.paramLocales[paramIndex] || this.$i18n.locale.split('-')[0]
    },
    
    updateParamLocale(paramIndex, locale) {
      this.$set(this.paramLocales, paramIndex, locale)
    },
    
    updateParamName(paramIndex, locale, value) {
      if (!this.step.parameters[paramIndex].nameTranslate) {
        this.$set(this.step.parameters[paramIndex], 'nameTranslate', {
          en: '',
          km: '',
          zh: '',
          vi: '',
        })
      }
      this.$set(this.step.parameters[paramIndex].nameTranslate, locale, value)
    },
  },
}
</script>

<style lang="scss" scoped>
  .border {
    border-radius: 4px;
    padding: 15px;
    margin-bottom: 15px;
    background-color: #f8f9fa;
  }

  .input-wrapper {
    min-height: 56px;  // Height of input + error message space
    display: flex;
    flex-direction: column;

    .d-flex {
      margin-bottom: 4px;  // Space between input and error message
    }
  }

  .question-section {
    background-color: #f8f9fa;
    border-radius: 0.5rem;
    margin-top: 1rem;

    ::v-deep {
      .custom-radio {
        margin-bottom: 0.5rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
</style>
